import React, { useCallback, useEffect, useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Stack } from '@mui/material'

import useSubscribeRefresh from 'use-subscribe-actions'
import LoaderSitesMetrics from 'components/Cartography/LoaderMetrics'
import TableSkeleton from 'components/global/CUK/Skeletons/TableSkeleton'
import { useRegisterBreadcrumbAction } from 'components/AuthenticatedLayout/Header/components/BreadcrumbActions'
import Radio from 'packages/ui/Radio'

import HardwareView from './HardwaresView'
import EquipmentsStatuses, {
  EquipmentsModels,
  EquipmentsManufacturers
} from '../../Topbar/Filters/EquipmentsFilter'
import TextFilter from '../../Topbar/Filters/TextFilter'
import ExportButton from '../../Topbar/ExportButton'
import { TableContent, MainContent } from '../../../PagesStyles'
import useEquipments, { TYPES } from '../use-equipments'
import { OPERATION_COUNT_EQUIPMENTS } from '../../operations-gql/queries'
import { useFilters, useResetFilters } from '../../store'
import { normalizeString } from '../helpers'
import useExport from './use-export'
import { HARDWARE_VIEW_MODES, useHardwareViewMode } from './use-hardware-view-mode'
import { HARDWARE_TYPES, supportsCompleteDetails } from './hardware-types'
import ViewPreferenceModal from './ViewPreferenceModal'

const getEquipmentCounts = (counting = {}) => {
  const counts = HARDWARE_TYPES.reduce((result, { countKey, okKey, topologyType }) => {
    const operational = Number(counting[`${topologyType}_ok`] || 0)
    result[countKey] = operational + Number(counting[`${topologyType}_unreachable`] || 0)
    result[okKey] = operational
    return result
  }, {})

  return {
    ...counts,
    nb_blends: HARDWARE_TYPES.reduce((total, { countKey }) => total + Number(counts[countKey] || 0), 0)
  }
}

const useFilteredEquipments = ({sites, hardwareType}) => {
  const apiEquipments = useEquipments({ sites, hardwareType })
  const [filters] = useFilters()

  const filterStatusFct = useCallback(
    ({ available_level, manufacturer }) => {
      if (manufacturer?.toLowerCase() === 'juniper') {
        return filters.status.includes('juniper')
      }
      return filters.status.includes(available_level === 'muted' ? 'planned' : available_level)
    },
    [filters.status]
  )

  const filterVerbatimFct = useCallback(
    equipment => {
      const normalizedVerbatim = normalizeString(filters.verbatim)
      return ['label', 'mac'].filter(column => {
        return equipment[column] && normalizeString(equipment[column]).includes(normalizedVerbatim)
      }).length
    },
    [filters.verbatim]
  )

  const filterModelsFct = useCallback(
    equipment => filters.models.includes(equipment.model)
    , [filters.models])

  const filterManufacturersFct = useCallback(
    equipment => filters.manufacturers.includes(equipment.manufacturer)
    , [filters.manufacturers])

  const equipmentsWithBlends = useMemo(() => {
    return Object.keys(TYPES).reduce((acc, key) => {
      return {
        ...acc,
        [key]: apiEquipments.equipments[key],
        blends: acc.blends.concat(apiEquipments.equipments[key])
      }
    }, { blends: [] })
  }, [apiEquipments])

  const filteredEquipments = useMemo(() => {
    const filtersFct = [
      filters.status.length ? filterStatusFct : null,
      filters.verbatim.length ? filterVerbatimFct : null,
      filters.models.length ? filterModelsFct : null,
      filters.manufacturers.length ? filterManufacturersFct : null
    ].filter(Boolean)
    if (filtersFct.length === 0) {
      return equipmentsWithBlends
    }
    return {
      ...equipmentsWithBlends,
      [hardwareType]: equipmentsWithBlends[hardwareType].filter(equipment => filtersFct.every(fct => fct(equipment)))
    }
  }, [equipmentsWithBlends, filters, filterStatusFct, filterVerbatimFct, filterModelsFct, filterManufacturersFct, hardwareType])

  return {
    equipments: filteredEquipments,
    loading: apiEquipments.loading
  }
}
const Hardware = ({ sites }) => {
  const { t } = useTranslation()
  const { hardwareType } = useParams()
  const { data, loading: countsLoading } = useQuery(OPERATION_COUNT_EQUIPMENTS, {
    variables: { sf_ids: sites.map(({ sf_id }) => sf_id) }
  })
  const equipmentCounts = useMemo(() => getEquipmentCounts(data?.counting), [data?.counting])
  const activeHardwareType = HARDWARE_TYPES.some(({ value }) => value === hardwareType)
    ? hardwareType
    : HARDWARE_TYPES.find(({ countKey }) => equipmentCounts[countKey] > 0)?.value || 'access_points'
  const { equipments, loading } = useFilteredEquipments({sites, hardwareType: activeHardwareType})
  const [filters, setFilters] = useFilters()
  const { viewMode, selectViewMode, buildSearch, canUseCompleteView, viewPreference } = useHardwareViewMode()

  const createExport = useExport({equipments})
  useRegisterBreadcrumbAction(
    <Stack direction="row" alignItems="center" gap={2}>
      {canUseCompleteView && supportsCompleteDetails(activeHardwareType) && (
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Radio
            checked={viewMode === HARDWARE_VIEW_MODES.SIMPLE}
            label={t('supervision.hardware.view.simple')}
            labelSx={{
              color: viewMode === HARDWARE_VIEW_MODES.SIMPLE ? '#292929' : '#656565'
            }}
            onChange={() => selectViewMode(HARDWARE_VIEW_MODES.SIMPLE)}
          />
          <Radio
            checked={viewMode === HARDWARE_VIEW_MODES.COMPLETE}
            label={t('supervision.hardware.view.complete')}
            labelSx={{
              color: viewMode === HARDWARE_VIEW_MODES.COMPLETE ? '#292929' : '#656565'
            }}
            onChange={() => selectViewMode(HARDWARE_VIEW_MODES.COMPLETE)}
          />
        </Stack>
      )}
      <ExportButton analyticsName="monitoring_equipments" onClick={async () => { await createExport() }} />
    </Stack>
  )

  const filterControls = (
    <Stack direction={'row'} gap={1} flexWrap="wrap" sx={{ minWidth: 0, maxWidth: '100%' }}>
      <EquipmentsStatuses
        equipments={equipments[activeHardwareType]}
        onChange={(values) => {
          setFilters({ ...filters, status: values })
        }}
        values={filters.status}
      />
      <EquipmentsModels
        equipments={equipments[activeHardwareType]}
        onChange={(values) => {
          setFilters({ ...filters, models: values })
        }}
        values={filters.models}
      />
      <EquipmentsManufacturers
        equipments={equipments[activeHardwareType]}
        onChange={(values) => {
          setFilters({ ...filters, manufacturers: values })
        }}
        values={filters.manufacturers}
      />
      <TextFilter />
    </Stack>
  )

  return (
    <>
      {viewPreference && <ViewPreferenceModal {...viewPreference} />}
      <MainContent mode={'expand'} sx={{ height: 0, minWidth: 0, width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
        <TableContent mode={'expand'} sx={{ flex: 1, minWidth: 0, width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
          {countsLoading ? (
            <TableSkeleton />
          ) : (
            <HardwareView
              loading={loading}
              count={equipmentCounts}
              equipments={equipments}
              filterControls={filterControls}
              viewMode={viewMode}
              buildSearch={buildSearch}
            />
          )}
        </TableContent>
      </MainContent>
    </>
  )
}
const Global = () => {
  const navigate = useNavigate()
  const [filters, setFilters] = useFilters()
  const resetFilters = useResetFilters()

  const location = useLocation()
  useSubscribeRefresh(() => {
    resetFilters()
    navigate('/monitoring/infras/hardwares')
  })

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search)
    const paramsString = queryParams.get('params')

    if (paramsString) {
      const decodedString = atob(paramsString)

      try {
        const paramsObject = JSON.parse(decodedString)
        const { filter } = paramsObject

        if (filter === 'up' || filter === 'down') {
          setFilters({ ...filters, status: [filter] })
        } else {
          setFilters({ ...filters, status: [] })
        }
      } catch (error) {
        console.error('Error parsing JSON:', error)
      }
    }
  }, [location.search, setFilters])

  return (
    <Stack gap={1} sx={{ flex: 1, minHeight: 0 }}>
      <LoaderSitesMetrics loader={<TableContent mode={'expand'}><TableSkeleton /></TableContent>}>
        {({ sites }) => (
          <Hardware sites={sites} />
        )}
      </LoaderSitesMetrics>
    </Stack>
  )
}

export default Global
